// Bank bulk-upload export dialog — shared by the salary export (Payroll
// Approval) and the expense export (Expense Management).
//
// Each bank produces its own column layout server-side (utils/bank_transfer_sheet
// in hrms). The `fields` below are the columns that CANNOT be derived from
// employee data — they are collected here after a bank is picked, and the
// backend writes each value to every row of the sheet as-is. Add a new bank by
// appending ONE entry here and a matching branch in the backend builder; both
// exports pick it up.
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { getMyProfile, type PayoutBank } from "../api/profile";

export interface BankExportField {
  key: string; // query param sent to the backend
  label: string;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
}

export interface BankExportFormat {
  id: string;
  label: string;
  fields: BankExportField[];
}

// Required stays required: the bank rejects a sheet with these columns blank,
// so Export is disabled until they're filled. My Profile → Bank Details only
// PREFILLS them — it doesn't relax the rule.
export const BANK_EXPORT_FORMATS: BankExportFormat[] = [
  {
    id: "kotak",
    label: "Kotak",
    fields: [
      { key: "debit_account_no", label: "Debit Account No", required: true, placeholder: "Company bank account to debit from" },
      { key: "sender_address", label: "Sender Address", required: true, placeholder: "e.g. DELHI" },
    ],
  },
  {
    id: "au",
    label: "AU Small Finance Bank",
    fields: [
      { key: "debit_account_no", label: "Debit Account No / IBAN", required: true, placeholder: "Company account / IBAN to debit from" },
      { key: "payment_method_name", label: "Payment Method Name", required: true, placeholder: "e.g. NEFT" },
      { key: "remitter_name", label: "Remitter Name", required: true, placeholder: "Company / remitter name" },
      { key: "payment_instructions", label: "Payment Instructions 1", required: true, placeholder: "e.g. Salary for the month" },
      { key: "ccy", label: "CCY", required: true, placeholder: "INR", defaultValue: "INR" },
    ],
  },
];

// My Profile → Bank Details supplies the same values, under its own names.
// Anything the profile hasn't filled in falls back to the field's default.
const PROFILE_TO_FIELD: Record<string, keyof PayoutBank> = {
  debit_account_no: "accountNumber",
  sender_address: "senderAddress",
  remitter_name: "remitterName",
  payment_method_name: "paymentMethodName",
  payment_instructions: "paymentInstructions",
  ccy: "ccy",
};

// Download name for a transfer sheet — mirrors `transfer_sheet_filename` in
// hrms/utils/bank_transfer_sheet.py (the backend's Content-Disposition), since
// the browser names the file from `link.download`, not the header.
// AU's bulk-upload portal wants the fixed AP43118079 prefix; whatever was typed
// in "Payment Instructions 1" follows it in UPPER CASE after an underscore
// (AP43118079_SALARY_SEPTEMBER.xlsx) so runs don't overwrite each other. Every
// other bank keeps the dated, descriptive name.
export const transferSheetFilename = (
  kind: "Salary" | "Expense",
  bank: string,
  year: number | string,
  month: number | string,
  fields: Record<string, string> = {}
): string => {
  if (bank === "au") {
    const suffix = (fields.payment_instructions ?? "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "_")
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
    return suffix ? `AP43118079_${suffix}.xlsx` : "AP43118079.xlsx";
  }
  return `${kind}_Bulk_Upload_${bank.toUpperCase()}_${year}_${String(month).padStart(2, "0")}.xlsx`;
};

export const seedBankFieldValues = (
  bankId: string,
  profileBank?: PayoutBank | null
): Record<string, string> => {
  const fmt = BANK_EXPORT_FORMATS.find((b) => b.id === bankId) ?? BANK_EXPORT_FORMATS[0];
  const seeded: Record<string, string> = {};
  fmt.fields.forEach((f) => {
    const fromProfile = profileBank?.[PROFILE_TO_FIELD[f.key]];
    seeded[f.key] = (fromProfile ?? "").toString().trim() || f.defaultValue || "";
  });
  return seeded;
};

// A small searchable dropdown that renders its menu INLINE (not in a portal),
// so it works inside a modal dialog — a portal-based dropdown gets blocked by
// the dialog's focus trap / pointer-events guard. Matches the input styling
// used by the surrounding form fields.
export const InlineSelect = ({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = "Select…",
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  ariaLabel?: string;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const q = query.trim().toLowerCase();
  const matched = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
      >
        <span className={current ? "text-gray-900" : "text-gray-400"}>
          {current?.label ?? placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-1"
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            aria-label={`Search ${ariaLabel || "options"}`}
            className="w-full text-sm px-2.5 py-1.5 mb-1 rounded-md border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="max-h-48 overflow-y-auto">
            {matched.map((o) => {
              const active = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors ${
                    active ? "bg-green-50 text-green-800 font-medium" : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  <span className="w-4 shrink-0">{active && <Check className="w-3.5 h-3.5" />}</span>
                  <span className="truncate flex-1">{o.label}</span>
                </button>
              );
            })}
            {q && matched.length === 0 && (
              <div className="px-2.5 py-2 text-sm text-gray-400">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export function BankExportDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  exporting = false,
  confirmClassName = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen bank id and the trimmed per-bank field values. */
  onConfirm: (bank: string, fields: Record<string, string>) => void;
  title: string;
  description?: string;
  exporting?: boolean;
  confirmClassName?: string;
}) {
  const [bank, setBank] = useState(BANK_EXPORT_FORMATS[0].id);
  const [values, setValues] = useState<Record<string, string>>(() =>
    seedBankFieldValues(BANK_EXPORT_FORMATS[0].id)
  );
  // The signed-in user's saved bank details (My Profile → Bank Details); they
  // pre-fill every field so an account number isn't retyped each run.
  const [profileBank, setProfileBank] = useState<PayoutBank | null>(null);

  // Reopening starts clean — a stale debit account from the last export is the
  // kind of thing nobody notices until the bank rejects the file. "Clean" means
  // back to the saved profile values, not blank.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const first = BANK_EXPORT_FORMATS[0].id;
    setBank(first);
    setValues(seedBankFieldValues(first, profileBank));
    // Refetch on open so a just-saved profile change is picked up immediately.
    getMyProfile()
      .then((p) => {
        if (cancelled) return;
        const saved: PayoutBank | null = p?.payout_bank ?? null;
        setProfileBank(saved);
        setValues(seedBankFieldValues(first, saved));
      })
      .catch(() => {
        /* no profile bank details → the typed-in fields still work */
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const format = useMemo(
    () => BANK_EXPORT_FORMATS.find((b) => b.id === bank) ?? BANK_EXPORT_FORMATS[0],
    [bank]
  );

  const complete = format.fields.every(
    (f) => !f.required || (values[f.key] ?? "").trim() !== ""
  );

  const confirm = () => {
    const trimmed: Record<string, string> = {};
    format.fields.forEach((f) => {
      trimmed[f.key] = (values[f.key] ?? "").trim();
    });
    onOpenChange(false);
    onConfirm(format.id, trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95%] sm:max-w-2xl rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description ??
              "Pick a bank, then fill the fields below — each value is written to every row of that bank's upload sheet."}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 sm:mt-3 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bank <span className="text-red-500">*</span>
            </label>
            <InlineSelect
              value={bank}
              onChange={(id) => {
                setBank(id);
                setValues(seedBankFieldValues(id, profileBank));
              }}
              options={BANK_EXPORT_FORMATS.map((b) => ({ value: b.id, label: b.label }))}
              ariaLabel="Bank"
              placeholder="Select a bank…"
            />
          </div>

          {/* Bank fields — two per row so the dialog stays short and wide. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {format.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {f.label} {f.required && <span className="text-red-500">*</span>}
                </label>
                <input
                  value={values[f.key] ?? ""}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                  }
                  placeholder={f.placeholder}
                  aria-label={f.label}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                />
              </div>
            ))}
          </div>

          <div className="flex gap-2 sm:gap-3 mt-4">
            <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1 text-sm">
              Cancel
            </Button>
            <Button
              onClick={confirm}
              disabled={exporting || !complete}
              className={`flex-1 text-sm ${confirmClassName}`}
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
